import {Game} from "../Game";
import {GameUtils} from "../utils/GameUtils";
import {gsap} from "gsap";
import {SymbolContainer} from "../reels/SymbolContainer";
import {SimpleReelModelState} from "../../core/reel/simplereel/SimpleReelModel";
import {SYMBOL} from "../enums/Enum";
import {LockFrame} from "./LockFrame";
import {Sound} from "../sound/Sound";
import {TimelineSprite} from "../../core/animation/TimelineSprite";

export class StickyScatter extends PIXI.Container {
    private readonly _reelId: number;
    private readonly _rowId: number;
    private _lockBar: LockFrame;
    private _chainBar: PIXI.Sprite;
    private _chainGlow: PIXI.Sprite;
    private _blinkTl: gsap.core.Timeline;
    private _scatter2Sym: SymbolContainer;
    private _isFirstScatter: boolean = false;

    constructor(reelId: number, rowId: number) {
        super();
        this._reelId = reelId;
        this._rowId = rowId;
        this.init();
    }

    public showHide(value: boolean): gsap.core.Timeline {
        return value ? this.show() : this.hide();
    }

    public chainGlowFadeIn(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this._chainGlow.alpha = 0;
        this._chainGlow.visible = true;
        return tl.to(this._chainGlow, {alpha: 1, duration: 0.2});
    }

    public stopBlink(): gsap.core.Tween {
        this._blinkTl?.paused();
        this._blinkTl?.kill();
        return this.chainGlowFadeOut();
    }

    public chainGlowFadeOut(): gsap.core.Tween {
        return gsap.to(this._chainGlow, {
            alpha: 0, duration: 0.4, onComplete: () => {
                this._chainGlow.visible = false;
            }
        });
    }

    destroy(options?: { children?: boolean; texture?: boolean; baseTexture?: boolean }) {
        this._lockBar.stopBlink();
        this._lockBar.destroy();
        this._chainBar.destroy();
        this._chainGlow.destroy();
        super.destroy(options);
    }

    public chainGlowBlink(): gsap.core.Timeline {
        this._chainGlow.visible = true;
        this._chainGlow.alpha = 0;
        this._blinkTl = new gsap.core.Timeline({
            repeat: 50
        });
        this._blinkTl.to(this._chainGlow, {alpha: 1, duration: 0.2});
        this._blinkTl.to(this._chainGlow, {duration: 0.1});
        this._blinkTl.to(this._chainGlow, {alpha: 0, duration: 0.2});
        return this._blinkTl;
    }

    private startZoomEffect(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this._chainBar.alpha = 0;
        this._chainBar.visible = true;
        this._lockBar.alpha = 0;
        this._lockBar.visible = true;

        const blast: TimelineSprite = this.createBlastEffect();
        this.addChildAt(blast, this.children.length - 3);
        tl.add(() => {
            blast.alpha = 1;
            if (this._isFirstScatter) {
                Game.soundPlayer.playSound(Sound.SYMBOL_LOCKED);
            }
        }, 0)
        tl.to([this._chainGlow, this._chainBar, this._lockBar], {duration: 0.001, alpha: 1}, 0);
        tl.add(blast.getAnimationAutoShowHide(true,true), 0);

        tl.fromTo([this._chainGlow.scale, this._chainBar.scale, this._lockBar.scale], {x: 6, y: 6}, {
            x: 0.9,
            y: 0.9,
            duration: 0.2
        }, 0);
        tl.add(() => {
            GameUtils.shake(0, 20, 60, 2, 0.1);
        }, 0.16);
        tl.to(this._scatter2Sym.scale, {x: 0.9, y: 0.9, duration: 0.05}, 0.15);

        tl.to(this._scatter2Sym.scale, {x: 1.1, y: 1.1, duration: 0.1}, ">");
        tl.to(this._scatter2Sym.scale, {x: 1, y: 1, duration: 0.1}, ">");

        tl.to([this._chainBar.scale, this._lockBar.scale], {x: 1.1, y: 1.1, duration: 0.1}, 0.2);
        tl.to([this._chainBar.scale, this._lockBar.scale], {x: 1, y: 1, duration: 0.1}, ">");
        tl.add(() => {
            blast.parent?.removeChild(blast);
        })
        return tl;
    }

    private createBlastEffect(): TimelineSprite {
        const blast: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("dead_blast/dead_blast_000"));
        blast.anchor.set(0.5);
        blast.alpha = 0;
        blast.position.set(0);
        return blast;
    }

    private show(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this.visible = true;
        this.alpha = 0;
        this._scatter2Sym.alpha = 0;
        tl.add([
            gsap.to(this, {alpha: 1, duration: 0.001}),
            gsap.to(this._scatter2Sym, {alpha: 1, duration: 0.4}),
            this.startZoomEffect(),
        ]);
        tl.add(() => {
            //this.chainGlowBlink();
            this._lockBar.glowBlink();
        });
        return tl;
    }

    private hide(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add([
            this._lockBar.stopBlink(),
            this.stopBlink(),
            gsap.to(this, {alpha: 0, duration: 0.1,})
        ]);
        tl.add(() => {
            this.visible = false;
        });
        return tl;
    }

    private createLockBar(): void {
        this._lockBar = new LockFrame(this._reelId, this._rowId);
        this._lockBar.position.set(0);
        this._lockBar.visible = true;
        this.addChild(this._lockBar);
    }

    private createChainBar(): void {
        const textureStr: string = (this._reelId === 2 && this._rowId === 0) ? "S1/S1_assets/chain_bar_half" : "S1/S1_assets/chain_bar";
        this._chainBar = new PIXI.Sprite(Game.assetLoader.getTextures(textureStr)[0]);
        this._chainBar.position.set(0);
        this._chainBar.anchor.set(0.5);
        this.addChild(this._chainBar);
    }

    private createChainGlow(): void {
        const textureStr: string = (this._reelId === 2 && this._rowId === 0) ? "S1/S1_assets/chain_bar_half_shine_00000" : "S1/S1_assets/chain_bar_shine_00000";
        this._chainGlow = new PIXI.Sprite(Game.assetLoader.getTextures(textureStr)[0]);
        this._chainGlow.anchor.set(0.5);
        this._chainGlow.position.set(0);
        this._chainGlow.visible = false;
        this.addChild(this._chainGlow);
    }

    private init(): void {
        const s2SymContainer: SymbolContainer = <SymbolContainer>Game.reelView.createSymbol(SYMBOL.S2, SimpleReelModelState.START, {
            reelID: this._reelId,
            position: this._rowId
        });
        s2SymContainer.position.set(0);
        this._scatter2Sym = s2SymContainer;
        const pos: PIXI.Point = GameUtils.getSymbolPos(this._reelId, this._rowId);
        this.position.set(pos.x, pos.y);
        this.alpha = 0;
        this.addChild(s2SymContainer);
        this.createLockBar();
        this.createChainBar();
        this.createChainGlow();
        this.visible = false;
    }

    set isFirstScatter(value: boolean) {
        this._isFirstScatter = value;
    }
}